<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, ref, useId } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { useValidators } from "@/composables/useValidators";
import CnmcFieldErrors from "./CnmcFieldErrors.vue";
import CnmcReasonForm from "./CnmcReasonForm.vue";
import { formatDateTime } from "@/composables/formatUtils";
import CnmcNotice from "./CnmcNotice.vue";
import { isIssuance, statusClass } from "./cnmcPresentation";
import type { CnmcApplication, CnmcApplicationAction } from "./cnmcTypes";

const props = withDefaults(
  defineProps<{
    application: CnmcApplication;
    busy: boolean;
    canPrepare: boolean;
    canReview?: boolean;
    isAdmin?: boolean;
  }>(),
  { canReview: false, isAdmin: false }
);
const emit = defineEmits<{ action: [action: CnmcApplicationAction] }>();
const id = useId();
const { locale, t } = useI18n();
const receipt = ref("");
const reason = ref("");
const validators = useValidators(t);
const rules = computed(() => ({
  receipt: {
    required: validators.required,
    format: helpers.withMessage(
      () => t("cnmc.receipt_format"),
      helpers.regex(/^[0-9]{4}03[0-9]{14}$/)
    ),
  },
  reason: props.application.receipt
    ? {
        required: validators.required,
        minLength: validators.minLength(5),
        maxLength: validators.maxLength(2000),
      }
    : {},
}));
const v$ = useVuelidate(
  rules,
  { receipt, reason },
  { $autoDirty: true, $stopPropagation: true }
);
const source = computed(() => isIssuance(props.application));
const receiptLabel = computed(() =>
  t(
    source.value
      ? "cnmc.issuance_receipt"
      : props.application.application_type === "TRANSFER"
        ? "cnmc.transfer_receipt"
        : "cnmc.export_receipt"
  )
);
const receiptHelp = computed(
  () =>
    `${t(source.value ? "cnmc.issuance_receipt_help" : "cnmc.delivery_receipt_help")} ${t("cnmc.receipt_format")}`
);
const receiptInput = computed(() => ({
  inputmode: "numeric" as const,
  placeholder: t("cnmc.receipt_example"),
  "aria-invalid": v$.value.receipt.$error,
  class: { "is-invalid": v$.value.receipt.$error },
  "aria-describedby": `${id}-receipt-help`,
}));
const editable = computed(
  () =>
    !["COMPLETED", "ABANDONED", "SUPERSEDED"].includes(props.application.status)
);
const workbook = computed(() =>
  source.value
    ? "Expedicion.xlsm"
    : props.application.application_type === "TRANSFER"
      ? "Transferencia.xlsm"
      : "Exportacion.xlsm"
);
const approved = computed(() =>
  ["APPROVED", "COMPLETED"].includes(props.application.status) &&
  props.application.latest_evidence
    ? props.application.latest_evidence.quantities.reduce(
        (sum, quantity) => sum + quantity.go_count,
        0
      )
    : null
);
const submit = () => {
  v$.value.$touch();
  if (props.busy || v$.value.$invalid) return;
  emit("action", {
    type: "submit",
    receipt: receipt.value,
    reason: props.application.receipt ? reason.value : undefined,
  });
};
</script>

<template>
  <article
    class="cnmc-application border rounded-3 p-3 h-100 d-flex flex-column gap-3"
  >
    <div class="d-flex justify-content-between align-items-start gap-2">
      <div class="min-width-0">
        <div
          v-if="isAdmin"
          class="alert alert-subtle-warning admin-extra-info d-inline-block px-1 py-0 small mb-1"
        >
          {{ $t("cnmc.application_number", { id: application.id }) }}
        </div>
        <h5 class="font-serif mb-0">
          {{ $t(`cnmc.kinds.${application.application_type}`) }}
        </h5>
        <p
          v-if="application.recipient"
          class="small text-body-secondary text-break mt-1 mb-0"
        >
          {{ application.recipient.name }} ·
          {{ application.recipient.r2 || application.recipient.eecs }}
        </p>
      </div>
      <span
        class="badge fw-medium text-wrap"
        :class="statusClass(application.status)"
        >{{ $t(`cnmc.statuses.${application.status}`) }}</span
      >
    </div>

    <dl class="mb-0 cnmc-application__facts">
      <div>
        <dt class="small text-body-secondary fw-normal">
          {{ $t("cnmc.requested") }}
        </dt>
        <dd class="fw-semibold mb-0">{{ $n(application.go_count) }} GOs</dd>
      </div>
      <div>
        <dt class="small text-body-secondary fw-normal">
          {{ $t("cnmc.approved") }}
        </dt>
        <dd class="fw-semibold mb-0">
          {{ approved === null ? "—" : `${$n(approved)} GOs` }}
        </dd>
      </div>
      <div v-if="!source">
        <dt class="small text-body-secondary fw-normal">
          {{ $t("cnmc.delivered") }}
        </dt>
        <dd class="fw-semibold mb-0">
          {{
            application.company_order_status === "COMPLETED"
              ? `${$n(application.delivered_go_count)} GOs`
              : "—"
          }}
        </dd>
      </div>
      <div>
        <dt class="small text-body-secondary fw-normal">
          {{ $t("cnmc.filing_deadline") }}
        </dt>
        <dd class="fw-semibold mb-0">{{ application.filing_deadline }}</dd>
      </div>
      <div>
        <dt class="small text-body-secondary fw-normal">
          {{ $t("cnmc.go_expiry") }}
        </dt>
        <dd class="fw-semibold mb-0">{{ application.expiry_date }}</dd>
      </div>
    </dl>
    <p v-if="application.source_receipts.length" class="small text-break mb-0">
      {{ $t("cnmc.source_receipts") }}:
      {{ application.source_receipts.join(", ") }}
    </p>
    <CnmcNotice v-if="application.error_code">
      {{ $t("cnmc.needs_attention_explanation") }}
      <code>{{ application.error_code }}</code>
    </CnmcNotice>
    <CnmcNotice v-if="application.file_outdated">{{
      $t("cnmc.outdated_file")
    }}</CnmcNotice>
    <CnmcNotice v-if="application.failure_count">{{
      $t("cnmc.lookup_retry")
    }}</CnmcNotice>

    <div
      class="cnmc-application__file rounded-3 p-2 d-flex align-items-center justify-content-between flex-wrap gap-2"
    >
      <div class="d-flex align-items-center gap-2">
        <FontAwesomeIcon icon="file" class="text-primary" />
        <div>
          <div class="small fw-medium">{{ workbook }}</div>
          <div class="small text-body-secondary">
            {{ $t("cnmc.zip_package") }}
          </div>
        </div>
      </div>
      <button
        v-if="application.can_download"
        class="btn btn-outline-secondary btn-sm"
        :disabled="busy"
        @click="emit('action', { type: 'download' })"
      >
        {{ $t("cnmc.download_zip") }}
      </button>
    </div>

    <form
      v-if="editable && !application.receipt"
      novalidate
      @submit.prevent="submit"
    >
      <label :for="`${id}-receipt`" class="form-label">{{
        receiptLabel
      }}</label>
      <div class="d-flex flex-wrap gap-2">
        <input
          :id="`${id}-receipt`"
          v-bind="receiptInput"
          v-model="receipt"
          @blur="v$.receipt.$touch()"
          class="form-control cnmc-application__receipt"
        />
        <button class="btn btn-primary btn-sm" :disabled="busy">
          {{ $t("cnmc.record_submission") }}
        </button>
      </div>
      <CnmcFieldErrors :validation="v$.receipt" />
      <div :id="`${id}-receipt-help`" class="form-text">
        {{ receiptHelp }}
      </div>
    </form>
    <div v-else-if="application.receipt">
      <div class="small text-body-secondary mb-1">
        {{ receiptLabel }}
      </div>
      <strong class="small text-break">{{ application.receipt }}</strong>
      <details v-if="editable" class="small mt-2">
        <summary>{{ $t("cnmc.correct_receipt") }}</summary>
        <form class="mt-2" novalidate @submit.prevent="submit">
          <label :for="`${id}-receipt`" class="form-label">{{
            receiptLabel
          }}</label>
          <input
            :id="`${id}-receipt`"
            v-bind="receiptInput"
            v-model="receipt"
            @blur="v$.receipt.$touch()"
            class="form-control"
          />
          <CnmcFieldErrors :validation="v$.receipt" />
          <div :id="`${id}-receipt-help`" class="form-text">
            {{ receiptHelp }}
          </div>
          <label :for="`${id}-reason`" class="form-label mt-2">{{
            $t("cnmc.correction_reason")
          }}</label>
          <input
            :id="`${id}-reason`"
            v-model.trim="reason"
            class="form-control"
            :class="{ 'is-invalid': v$.reason.$error }"
            :aria-invalid="v$.reason.$error"
            @blur="v$.reason.$touch()"
          />
          <CnmcFieldErrors :validation="v$.reason" />
          <button class="btn btn-outline-primary btn-sm mt-2" :disabled="busy">
            {{ $t("cnmc.record_submission") }}
          </button>
        </form>
      </details>
    </div>

    <details v-if="application.latest_evidence" class="small">
      <summary>
        {{ $t("cnmc.registry_evidence") }}:
        {{ application.latest_evidence.raw_state }}
      </summary>
      <p v-if="application.latest_evidence.reason" class="my-2">
        {{ application.latest_evidence.reason }}
      </p>
      <ul class="mt-2 mb-0 ps-3 text-break">
        <li
          v-for="(quantity, index) in application.latest_evidence.quantities"
          :key="index"
        >
          {{ quantity.cil }} · {{ quantity.month }} ·
          {{ quantity.go_count }} GOs
          <span v-if="quantity.source_receipt">
            · {{ quantity.source_receipt }}</span
          >
        </li>
      </ul>
    </details>
    <details v-if="canPrepare && editable" class="small">
      <summary>{{ $t("cnmc.replace_file") }}</summary>
      <p class="my-2">{{ $t("cnmc.replace_file_help") }}</p>
      <CnmcReasonForm
        :label="$t('cnmc.correction_reason')"
        :submit-label="$t('cnmc.replace_file')"
        :disabled="busy"
        @submit="emit('action', { type: 'replace', reason: $event })"
      />
    </details>
    <details v-if="canReview && application.error_code" class="small">
      <summary>{{ $t("cnmc.staff_review") }}</summary>
      <p class="my-2">{{ $t("cnmc.staff_review_help") }}</p>
      <CnmcReasonForm
        :label="$t('cnmc.correction_reason')"
        :submit-label="$t('cnmc.resume_verification')"
        :disabled="busy"
        @submit="emit('action', { type: 'resolve', reason: $event })"
      />
    </details>

    <footer class="border-top pt-3 mt-auto small">
      <dl v-if="application.company_order_id" class="mb-2">
        <div>
          <dt class="text-body-secondary fw-normal">
            {{ $t("cnmc.company_order") }}
          </dt>
          <dd class="mb-0">
            #{{ application.company_order_id }} ·
            {{ application.company_order_status }}
          </dd>
        </div>
      </dl>
      <div class="d-flex justify-content-between align-items-center gap-2">
        <div class="text-body-secondary">
          {{ $t("cnmc.last_checked") }} ·
          {{
            formatDateTime(application.last_checked_at, locale) ||
            $t("cnmc.not_checked")
          }}
          <div v-if="application.next_check_at">
            {{ $t("cnmc.next_check") }} ·
            {{ formatDateTime(application.next_check_at, locale) }}
          </div>
        </div>
        <button
          v-if="application.receipt"
          class="btn btn-link btn-sm p-0 flex-shrink-0"
          :disabled="busy"
          @click="emit('action', { type: 'check' })"
        >
          {{ $t("cnmc.check_status") }}
        </button>
      </div>
    </footer>
  </article>
</template>

<style scoped lang="scss">
.cnmc-application {
  background: var(--phoenix-emphasis-bg);
}
.cnmc-application__file {
  background: var(--phoenix-gray-50);
}
.cnmc-application__facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 100px), 1fr));
  gap: 12px;
  font-variant-numeric: tabular-nums;
}
.cnmc-application__receipt {
  flex: 1 1 160px;
  min-width: 0;
}
</style>
